import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function ProductDetail() {
  const { id } = useParams();
  const { add } = useCart();
  const [p, setP] = useState(null);
  const [idx, setIdx] = useState(0);
  const [threshold, setThreshold] = useState(3);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => setP(r.data)).catch(() => setP(false));
    api.get("/settings").then((r) => setThreshold(parseInt(r.data.low_stock_threshold, 10) || 3)).catch(() => {});
  }, [id]);

  if (p === null) return <div className="p-16 text-center text-zinc-500">Loading…</div>;
  if (p === false) return <div className="p-16 text-center text-zinc-500">Product not found. <Link to="/shop" className="text-[var(--cl-orange)]">Back to Shop</Link></div>;

  const images = p.images && p.images.length ? p.images : ["https://images.unsplash.com/photo-1599873265732-4511510f0908"];
  const onSale = p.compare_at_price && p.compare_at_price > p.price;
  const pctOff = onSale ? Math.round((1 - p.price / p.compare_at_price) * 100) : 0;
  const stockBadge = p.stock === 1 ? { text: "LAST ONE", pulse: true }
                   : (typeof p.stock === "number" && p.stock > 0 && p.stock <= threshold) ? { text: `Only ${p.stock} left`, pulse: false }
                   : null;

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-12">
      <div className="grid md:grid-cols-2 gap-10">
        <div>
          <div className="aspect-[3/4] rounded-md overflow-hidden bg-[var(--cl-surface)] border border-white/10">
            <img src={images[idx]} alt={p.title} className="w-full h-full object-cover" data-testid="product-image" />
          </div>
          {images.length > 1 && (
            <div className="flex gap-2 mt-3">
              {images.map((im, i) => (
                <button key={im} onClick={() => setIdx(i)} className={`w-20 h-24 rounded border ${i === idx ? "border-[var(--cl-primary)]" : "border-white/10"}`}>
                  <img src={im} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          <div className="text-label text-zinc-500">{p.sport} · {p.year}</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2">{p.title}</h1>
          <div className="mt-4 flex flex-wrap items-baseline gap-3">
            {onSale && (
              <span data-testid="detail-was-price"
                    className="font-display text-2xl text-[var(--cl-primary)] line-through decoration-2">
                ${p.compare_at_price.toLocaleString()}
              </span>
            )}
            <span data-testid="detail-now-price"
                  className={`font-display ${onSale ? "text-5xl" : "text-4xl"}`}
                  style={{ color: "var(--cl-primary)" }}>
              ${p.price.toLocaleString()}
            </span>
            {onSale && (
              <span className="text-xs bg-[var(--cl-primary)] text-white px-2 py-1 rounded font-bold tracking-widest uppercase">
                Save {pctOff}%
              </span>
            )}
          </div>
          {stockBadge && (
            <div data-testid="detail-stock-badge"
                 className={`inline-block mt-4 text-xs px-3 py-1 rounded font-bold tracking-widest uppercase ${stockBadge.pulse ? "bg-[var(--cl-primary)] text-white animate-pulse" : "border border-[var(--cl-orange)]/60 text-[var(--cl-orange)]"}`}>
              {stockBadge.text}
            </div>
          )}

          <div className="grid grid-cols-2 gap-x-6 gap-y-3 mt-8 text-sm">
            {p.set_name && <Row label="Set" value={p.set_name} />}
            {p.player && <Row label="Player" value={p.player} />}
            {p.parallel && <Row label="Parallel" value={p.parallel} />}
            {p.serial_number && <Row label="Serial" value={p.serial_number} />}
            {p.condition && <Row label="Condition" value={p.condition.toUpperCase()} />}
            {p.attributes?.length > 0 && <Row label="Tags" value={p.attributes.join(", ")} />}
          </div>

          {p.description && <p className="text-zinc-400 mt-8 leading-relaxed">{p.description}</p>}

          <button className="btn-primary mt-10 w-full sm:w-auto" data-testid="detail-add-to-cart" onClick={() => add(p)}>
            Add to Cart
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <div className="text-label text-zinc-500">{label}</div>
      <div className="text-white">{value}</div>
    </div>
  );
}
